import { useEffect, useState } from 'react';
import { studioPsa } from '@/lib/api';

interface PsaStatus {
  connected: boolean;
  psa_type?: string;
  sync_status?: string;
  last_synced_at?: string | null;
  cached_data?: {
    company_count?: number;
    open_ticket_count?: number;
    device_count?: number;
  };
}

export function PSAConnection() {
  const [status, setStatus] = useState<PsaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const data = await studioPsa.status();
        setStatus(data as PsaStatus);
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  async function handleConnect(e: React.FormEvent) {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setConnecting(true);
    setError(null);
    try {
      await studioPsa.connect({ api_key: apiKey.trim() });
      setSuccess(true);
      const data = await studioPsa.status();
      setStatus(data as PsaStatus);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed');
    } finally {
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    setConnecting(true);
    try {
      await studioPsa.disconnect();
      setStatus({ connected: false });
      setSuccess(false);
    } finally {
      setConnecting(false);
    }
  }

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">PSA Connection</h1>
        <p className="text-muted-foreground mt-1">Connect your PSA to auto-populate client data in your apps</p>
      </div>

      {loading ? (
        <div className="h-32 bg-muted animate-pulse rounded-xl" />
      ) : status?.connected ? (
        <div className="border border-border rounded-xl p-6 bg-card space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-3 w-3 rounded-full bg-green-500" />
            <p className="font-medium text-foreground">Connected{status.psa_type ? ` — ${status.psa_type}` : ''}</p>
          </div>
          {status.cached_data && (
            <div className="grid grid-cols-3 gap-4 pt-2">
              {status.cached_data.company_count !== undefined && (
                <div><p className="text-xl font-bold text-foreground">{status.cached_data.company_count}</p><p className="text-xs text-muted-foreground">Companies</p></div>
              )}
              {status.cached_data.open_ticket_count !== undefined && (
                <div><p className="text-xl font-bold text-foreground">{status.cached_data.open_ticket_count}</p><p className="text-xs text-muted-foreground">Open Tickets</p></div>
              )}
              {status.cached_data.device_count !== undefined && (
                <div><p className="text-xl font-bold text-foreground">{status.cached_data.device_count}</p><p className="text-xs text-muted-foreground">Devices</p></div>
              )}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Last synced: {status.last_synced_at ? new Date(status.last_synced_at).toLocaleString() : 'Never'}
          </p>
          <button
            onClick={() => void handleDisconnect()}
            disabled={connecting}
            className="text-sm text-red-500 hover:text-red-700 disabled:opacity-50"
          >
            Disconnect PSA
          </button>
        </div>
      ) : (
        <div className="border border-border rounded-xl p-6 bg-card">
          <p className="text-sm text-muted-foreground mb-4">
            Connecting your PSA unlocks personalized app recommendations, auto-populated client data, and ticket visibility features.
          </p>
          {success && <p className="text-sm text-green-600 mb-3">Connected successfully!</p>}
          <form onSubmit={(e) => void handleConnect(e)} className="space-y-3">
            <input
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="PSA API key"
              required
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button
              type="submit"
              disabled={connecting || !apiKey.trim()}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {connecting ? 'Connecting…' : 'Connect PSA'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
